// src/api/useGlobalSearch.js
import { useEffect, useState } from "react";
import axios from "axios";

const getLocale = () => {
  const match = document.cookie.match(/(^|;)\s*locale=([^;]*)/);
  return match ? decodeURIComponent(match[2]) : "en";
};

const useGlobalSearch = (query) => {
  const [events, setEvents] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
  const STRAPI_TOKEN = process.env.REACT_APP_STRAPI_TOKEN;
  const WOO_BASE_URL = process.env.REACT_APP_WOO_URL;
  const WOO_KEY = process.env.REACT_APP_WOO_CONSUMER_KEY;
  const WOO_SECRET = process.env.REACT_APP_WOO_CONSUMER_SECRET;
  const locale = getLocale();

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setEvents([]);
      setRecipes([]);
      setPortfolios([]);
      setProducts([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const headers = {
        Authorization: `Bearer ${STRAPI_TOKEN}`,
        "Content-Type": "application/json",
      };

      try {
        // Events
        const eventsUrl = new URL(`${STRAPI_URL}/api/events`);
        eventsUrl.searchParams.set("locale", locale);
        eventsUrl.searchParams.set("filters[title][$containsi]", query);
        eventsUrl.searchParams.set("pagination[limit]", "5");

        const resEvents = await fetch(eventsUrl.toString(), { headers });
        const eventsJson = await resEvents.json();
        setEvents(eventsJson.data || []);

        // Recipes
        const recipesUrl = new URL(`${STRAPI_URL}/api/recipes`);
        recipesUrl.searchParams.set("locale", locale);
        recipesUrl.searchParams.set("filters[title][$containsi]", query);
        recipesUrl.searchParams.set("pagination[limit]", "5");

        const resRecipes = await fetch(recipesUrl.toString(), { headers });
        const recipesJson = await resRecipes.json();
        setRecipes(recipesJson.data || []);

        // Portfolios
        const portfoliosUrl = new URL(`${STRAPI_URL}/api/portfolios`);
        portfoliosUrl.searchParams.set("locale", locale);
        portfoliosUrl.searchParams.set("filters[title][$containsi]", query);
        portfoliosUrl.searchParams.set("pagination[limit]", "5");

        const resPortfolios = await fetch(portfoliosUrl.toString(), { headers });
        const portfoliosJson = await resPortfolios.json();
        setPortfolios(portfoliosJson.data || []);

        // WooCommerce Products (category 60, published only)
        const wooRes = await axios.get(`${WOO_BASE_URL}/products`, {
          auth: {
            username: WOO_KEY,
            password: WOO_SECRET,
          },
          params: {
            category: 60,
            status: "publish",
            search: query,
            per_page: 5,
          },
        });
        setProducts(wooRes.data || []);
      } catch (err) {
        console.error("❌ Global search error:", err);
        setError("Global search failed");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [query, STRAPI_URL, STRAPI_TOKEN, WOO_BASE_URL, WOO_KEY, WOO_SECRET, locale]);

  return { events, recipes, portfolios, products, loading, error };
};

export default useGlobalSearch;
